"use client";

import { useEffect, useState } from "react";
import { ArrowRight, ArrowDown } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
export default function Unified() {
  const [forecastIndex, setForecastIndex] = useState(0);
  const [underlineWidth, setUnderlineWidth] = useState(0);
  useEffect(() => {
    const target = 99.7;
    const duration = 1800;
    const start = performance.now();
    let raf = 0;
    const tick = (t: number) => {
      const p = Math.min(1, (t - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setForecastIndex(Number((eased * target).toFixed(1)));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    const tUnderline = setTimeout(() => setUnderlineWidth(100), 600);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(tUnderline);
    };
  }, []);
  const particles = Array.from({
    length: 18
  }, (_, i) => ({
    left: i * 53 % 100,
    delay: i * 0.7 % 8,
    duration: 9 + i * 1.3 % 7,
    size: i % 3 + 1,
    color: i % 4 === 0 ? "var(--chart-2)" : "var(--chart-1)"
  }));
  const stats = [{
    label: "Network Uptime",
    value: "99.7%",
    trend: "up"
  }, {
    label: "Security Response",
    value: "-12ms",
    trend: "up"
  }, {
    label: "System Latency",
    value: "4.1ms",
    trend: "down"
  }];
  return <section data-section-id="3371" id="the-future-of-unified-it" className="relative bg-background text-foreground overflow-hidden flex items-center">
      {}
      <div className="absolute inset-0 opacity-[0.04] pointer-events-none animate-grid-drift" style={{
      backgroundImage: "linear-gradient(var(--foreground) 1px, transparent 1px), linear-gradient(90deg, var(--foreground) 1px, transparent 1px)",
      backgroundSize: "80px 80px"
    }} aria-hidden="true" />

      {}
      <div className="absolute bottom-[-20%] right-[-10%] w-[800px] h-[800px] rounded-lg pointer-events-none animate-orb-rotate opacity-60" style={{
      background: "conic-gradient(from 0deg, var(--chart-1) 0%, transparent 40%, transparent 100%)",
      filter: "blur(60px)"
    }} aria-hidden="true" />

      {}
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        {particles.map((p, i) => <span key={i} data-index={i} className="absolute rounded-lg animate-particle-rise" style={{
        left: `${p.left}%`,
        bottom: "-10px",
        width: `${p.size}px`,
        height: `${p.size}px`,
        backgroundColor: p.color,
        animationDelay: `${p.delay}s`,
        animationDuration: `${p.duration}s`,
        boxShadow: `0 0 8px ${p.color}`
      }} />)}
      </div>

      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-8 pt-10 pb-16 lg:pt-12 lg:pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          {}
          <div className="lg:col-span-7">
            {}
            <div className="flex items-center gap-3 mb-8 font-mono text-xs tracking-widest text-muted-foreground">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-lg opacity-75" style={{
                backgroundColor: "var(--chart-1)"
              }} />
                <span className="relative inline-flex rounded-lg h-2 w-2" style={{
                backgroundColor: "var(--chart-1)"
              }} />
              </span>
              <span>BETAQUEST.LLC — UNIFIED IT ARCHITECTURE</span>
            </div>

            {}
            <h1 className="font-default font-bold text-4xl sm:text-5xl md:text-6xl lg:text-[3.5rem] xl:text-[4rem] leading-[0.95] tracking-tight mb-8">
              Unified Solutions,{" "}
              <span className="italic font-normal">engineered</span> for{" "}
              <span className="relative inline-block">
                modernity.
                <span className="absolute left-0 bottom-1 h-[3px] transition-all duration-1000 ease-out" style={{
                width: `${underlineWidth}%`,
                backgroundColor: "var(--chart-1)"
              }} />
              </span>
            </h1>

            {}
            <p className="text-xl text-muted-foreground max-w-xl mb-10 leading-relaxed">
              Betaquest, LLC specializes in unified IT solutions tailored to your business needs. Our systems integrate seamlessly to improve performance, security, and scalability.
            </p>

            {}
            <div className="flex flex-wrap items-center gap-4">
              <Button asChild size="lg" className="rounded-lg h-12 px-7 bg-primary text-primary-foreground hover:bg-primary/90 font-bold">
                <Link to="/SolutionsPage">
                  VIEW SOLUTIONS
                  <ArrowRight className="ml-1 h-4 w-4" />
                </Link>
              </Button>
              <Button asChild variant="ghost" size="lg" className="rounded-lg h-12 px-5 text-foreground hover:bg-muted group font-bold">
                <Link to="/HomePage">
                  ABOUT US
                  <ArrowRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Link>
              </Button>
            </div>
          </div>

          {}
          <div className="lg:col-span-5 group">
            <div className="relative h-[420px] lg:h-[460px] rounded-lg border border-border p-6 lg:p-7 transition-transform duration-500 group-hover:-rotate-1 group-hover:scale-[1.02]" style={{
            background: "linear-gradient(145deg, hsla(260, 15%, 6%, 0.9), hsla(260, 15%, 4%, 0.8))",
            backdropFilter: "blur(20px)",
            WebkitBackdropFilter: "blur(20px)",
            boxShadow: "0 25px 50px -12px hsla(270, 100%, 64%, 0.2), inset 0 1px 0 hsla(0, 0%, 100%, 0.06)"
          }}>
              {}
              <div className="flex items-center justify-between mb-6">
                <span className="font-mono text-[10px] tracking-widest text-muted-foreground">
                  SYSTEM // PERFORMANCE METRICS
                </span>
                <span className="flex items-center gap-2 font-mono text-[10px] text-muted-foreground">
                  <span className="h-1.5 w-1.5 rounded-lg animate-pulse" style={{
                  backgroundColor: "var(--chart-1)"
                }} />
                  ONLINE
                </span>
              </div>

              {}
              <div className="mb-6">
                <div className="font-default text-7xl lg:text-8xl font-bold leading-none tabular-nums">
                  {forecastIndex.toFixed(1)}%
                </div>
                <div className="font-mono text-xs text-muted-foreground mt-2">
                  UPTIME
                </div>
              </div>

              {}
              <div className="mb-6 h-20 w-full">
                <svg viewBox="0 0 400 80" className="w-full h-full" preserveAspectRatio="none">
                  <defs>
                    <linearGradient id="sparkFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="var(--chart-1)" stopOpacity="0.3" />
                      <stop offset="100%" stopColor="var(--chart-1)" stopOpacity="0" />
                    </linearGradient>
                  </defs>
                  <path d="M0,55 C40,50 60,60 90,45 C120,30 150,40 180,25 C210,12 240,35 270,20 C300,8 330,28 360,15 C380,8 390,12 400,10 L400,80 L0,80 Z" fill="url(#sparkFill)" />
                  <path d="M0,55 C40,50 60,60 90,45 C120,30 150,40 180,25 C210,12 240,35 270,20 C300,8 330,28 360,15 C380,8 390,12 400,10" fill="none" stroke="var(--chart-1)" strokeWidth="2" strokeLinecap="round" className="animate-spark-draw" />
                  {}
                  <circle cx="180" cy="25" r="3" fill="var(--chart-2)" />
                  <circle cx="270" cy="20" r="3" fill="var(--chart-2)" />
                  <circle cx="360" cy="15" r="3" fill="var(--chart-2)" />
                </svg>
              </div>

              {}
              <div className="space-y-2.5 pt-4 border-t border-border">
                {stats.map((stat, i) => <div key={stat.label} data-index={i} className="flex items-center justify-between text-xs">
                    <span className="font-mono text-muted-foreground">
                      {stat.label}
                    </span>
                    <div className="flex items-center gap-3 flex-1 mx-4 max-w-[120px]">
                      <svg viewBox="0 0 80 20" className="w-full h-4" preserveAspectRatio="none">
                        <path d={stat.trend === "up" ? "M0,15 L20,12 L40,8 L60,5 L80,3" : "M0,5 L20,8 L40,10 L60,13 L80,15"} fill="none" stroke={stat.trend === "up" ? "var(--chart-1)" : "var(--destructive)"} strokeWidth="1.5" />
                      </svg>
                    </div>
                    <span className="font-mono font-bold tabular-nums" style={{
                  color: stat.trend === "up" ? "var(--chart-1)" : "var(--destructive)"
                }}>
                      {stat.value}
                    </span>
                  </div>)}
              </div>
            </div>
          </div>
        </div>

        {}
        <div className="hidden md:flex absolute bottom-6 left-6 lg:left-8 font-mono text-[10px] text-muted-foreground tracking-wider">
          // SECURE.INTEGRATED.SCALABLE — BETAQUEST NODE
        </div>
        <div className="hidden md:flex absolute bottom-6 right-6 lg:right-8 flex-col items-center gap-2 font-mono text-[10px] text-muted-foreground tracking-widest">
          <span className="[writing-mode:vertical-rl] rotate-180">SCROLL</span>
          <ArrowDown className="h-3 w-3 animate-scroll-bounce" />
        </div>
      </div>

      <style>{`
        @keyframes orb-rotate {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .animate-orb-rotate {
          animation: orb-rotate 40s linear infinite;
        }
        @keyframes grid-drift {
          0%, 100% { transform: translate(0, 0); }
          50% { transform: translate(20px, 20px); }
        }
        .animate-grid-drift {
          animation: grid-drift 30s ease-in-out infinite;
        }
        @keyframes particle-rise {
          0% { transform: translateY(0) translateX(0); opacity: 0; }
          10% { opacity: 1; }
          90% { opacity: 1; }
          100% { transform: translateY(-110vh) translateX(20px); opacity: 0; }
        }
        .animate-particle-rise {
          animation: particle-rise linear infinite;
        }
        @keyframes spark-draw {
          from { stroke-dasharray: 1000; stroke-dashoffset: 1000; }
          to { stroke-dasharray: 1000; stroke-dashoffset: 0; }
        }
        .animate-spark-draw {
          animation: spark-draw 2.5s ease-out forwards;
        }
        @keyframes scroll-bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(4px); }
        }
        .animate-scroll-bounce {
          animation: scroll-bounce 1.8s ease-in-out infinite;
        }
      `}</style>
    </section>;
}